import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Province from 'components/blocks/Delta/Inputs/Province';
import FormInputText from './FormInputTexto';

type FieldKey = 'nombreEmpresa' | 'ccc' | 'naf' | 'cif';

interface IRelacionesLaboralesNames {
    nombreEmpresa: string;
    ccc: string;
    naf: string;
    cif: string;
    esAutonomo?: string;
    provinciaCodigo?: string;
    provinciaDescripcion?: string;
}

interface IRelacionesLaboralesLabels {
    selector?: string;
    nombreEmpresa?: string;
    ccc?: string;
    naf?: string;
    cif?: string;
}

interface IRelacionesLaboralesDataKeys {
    id?: string | string[];
    label?: string | string[];
    nombreEmpresa?: string | string[];
    ccc?: string | string[];
    naf?: string | string[];
    cif?: string | string[];
    esAutonomo?: string | string[];
    provinciaCodigo?: string | string[];
    provinciaDescripcion?: string | string[];
}

interface IFormRelacionesLaboralesProps {
    names: IRelacionesLaboralesNames;
    relacionesLaborales?: any[] | null;
    info?: boolean;
    labels?: IRelacionesLaboralesLabels;
    dataKeys?: IRelacionesLaboralesDataKeys;
    selectorName?: string;
    selectorPlaceholder?: string;
    showProvince?: boolean;
    md?: number;
    xl?: number;
}

const DEFAULT_DATA_KEYS: Record<'id' | 'label' | FieldKey, string[]> = {
    id: ['EntornoLaboralId', 'EntornoLaboralEmpresaId', 'Id', 'id'],
    label: ['Nombre', 'RazonSocial', 'PrestacionRazonSocial', 'NombreEmpresa', 'name'],
    nombreEmpresa: ['NombreEmpresa', 'RazonSocial', 'PrestacionRazonSocial', 'Nombre', 'nombreEmpresa', 'razonSocial'],
    ccc: ['CCC', 'PrestacionCCC', 'EmpresaCCC', 'CodigoCuentaCotizacion', 'ccc', 'prestacionCCC', 'empresaCCC'],
    naf: ['NAF', 'PrestacionNAF', 'naf', 'prestacionNAF'],
    cif: ['CIF', 'PrestacionCIF', 'cif', 'prestacionCIF'],
};

const DEFAULT_ES_AUTONOMO_KEYS = ['EsAutonomo', 'esAutonomo'];

const DEFAULT_PROVINCE_DATA_KEYS = {
    provinciaCodigo: ['CodigoProvincia', 'ProvinciaCodigo', 'Provincia', 'codigoProvincia', 'provinciaCodigo', 'provincia'],
    provinciaDescripcion: ['ProvinciaDescripcion', 'NombreProvincia', 'provinciaDescripcion', 'nombreProvincia'],
};

const normalizeEmptyValue = (value: any) => {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'string' && value.trim() === '') {
        return null;
    }

    return value;
};

const hasValue = (value: any) => normalizeEmptyValue(value) !== null;

const areSameValues = (valueA: any, valueB: any) => {
    return normalizeEmptyValue(valueA) === normalizeEmptyValue(valueB);
};

const toArray = (value?: string | string[]) => {
    if (!value) {
        return [];
    }

    return Array.isArray(value) ? value : [value];
};

const getFirstValueByKeys = (source: any, keys: string[]) => {
    if (!source || !keys.length) {
        return null;
    }

    for (let index = 0; index < keys.length; index += 1) {
        const key = keys[index];
        const value = normalizeEmptyValue(source?.[key]);
        if (hasValue(value)) {
            return value;
        }
    }

    return null;
};

export const normalizeRelacionLaboralBoolean = (value: any) => {
    if (value === true || value === 'true' || value === 1 || value === '1') return true;
    if (value === false || value === 'false' || value === 0 || value === '0') return false;
    return null;
};

const FormRelacionesLaborales: React.FC<IFormRelacionesLaboralesProps> = ({
    names,
    relacionesLaborales,
    info = false,
    labels,
    dataKeys,
    selectorName,
    selectorPlaceholder = 'Selecciona un entorno laboral',
    showProvince = false,
    md = 12,
    xl = 12,
}) => {
    const { control, register, setValue, formState: { errors } } = useFormContext();
    const shouldUseRelacionesLaborales = info === true;
    const shouldShowProvince = showProvince === true && !!names.provinciaCodigo && !!names.provinciaDescripcion;

    const entornos = useMemo(() => {
        return shouldUseRelacionesLaborales && Array.isArray(relacionesLaborales) ? relacionesLaborales : [];
    }, [relacionesLaborales, shouldUseRelacionesLaborales]);

    const mergedLabels = useMemo(() => {
        return {
            selector: labels?.selector || 'Entorno laboral',
            nombreEmpresa: labels?.nombreEmpresa || 'Nombre Empresa',
            ccc: labels?.ccc || 'CCC',
            naf: labels?.naf || 'NAF',
            cif: labels?.cif || 'CIF',
        };
    }, [labels]);

    const mergedDataKeys = useMemo(() => {
        return {
            id: [...toArray(dataKeys?.id), ...DEFAULT_DATA_KEYS.id],
            label: [...toArray(dataKeys?.label), ...DEFAULT_DATA_KEYS.label],
            nombreEmpresa: [...toArray(dataKeys?.nombreEmpresa), ...DEFAULT_DATA_KEYS.nombreEmpresa],
            ccc: [...toArray(dataKeys?.ccc), ...DEFAULT_DATA_KEYS.ccc],
            naf: [...toArray(dataKeys?.naf), ...DEFAULT_DATA_KEYS.naf],
            cif: [...toArray(dataKeys?.cif), ...DEFAULT_DATA_KEYS.cif],
            esAutonomo: [...toArray(dataKeys?.esAutonomo), ...DEFAULT_ES_AUTONOMO_KEYS],
            provinciaCodigo: [...toArray(dataKeys?.provinciaCodigo), ...DEFAULT_PROVINCE_DATA_KEYS.provinciaCodigo],
            provinciaDescripcion: [...toArray(dataKeys?.provinciaDescripcion), ...DEFAULT_PROVINCE_DATA_KEYS.provinciaDescripcion],
        };
    }, [dataKeys]);

    const getEntornoOptionValue = useCallback((entorno: any, index: number) => {
        const id = getFirstValueByKeys(entorno, mergedDataKeys.id);
        return String(normalizeEmptyValue(id) ?? index);
    }, [mergedDataKeys.id]);

    const getEntornoOptionLabel = useCallback((entorno: any, index: number) => {
        const labelByKey = getFirstValueByKeys(entorno, mergedDataKeys.label);
        const labelByName = getFirstValueByKeys(entorno, mergedDataKeys.nombreEmpresa);

        if (hasValue(labelByKey)) {
            return String(labelByKey);
        }

        if (hasValue(labelByName)) {
            return String(labelByName);
        }

        return `Entorno laboral ${index + 1}`;
    }, [mergedDataKeys.label, mergedDataKeys.nombreEmpresa]);

    const getEntornoFieldValue = useCallback((entorno: any, fieldKey: FieldKey) => {
        if (!entorno) {
            return null;
        }

        return getFirstValueByKeys(entorno, mergedDataKeys[fieldKey]);
    }, [mergedDataKeys]);

    const [selectedEntornoId, setSelectedEntornoId] = useState<string | null>(null);

    const selectedEntorno = useMemo(() => {
        if (entornos.length === 0) {
            return null;
        }

        if (!selectedEntornoId) {
            return entornos[0];
        }

        const entornoEncontrado = entornos.find((entorno, index) => {
            return getEntornoOptionValue(entorno, index) === selectedEntornoId;
        });

        return entornoEncontrado || entornos[0];
    }, [entornos, selectedEntornoId, getEntornoOptionValue]);

    const watchedValues = useWatch({
        control,
        name: [
            names.nombreEmpresa,
            names.ccc,
            names.naf,
            names.cif,
            names.esAutonomo || '__FormRelacionesLaboralesEsAutonomo',
            names.provinciaCodigo || '__FormRelacionesLaboralesProvinciaCodigo',
            names.provinciaDescripcion || '__FormRelacionesLaboralesProvinciaDescripcion',
        ],
    }) as any[];

    const watchedNombreEmpresa = watchedValues?.[0];
    const watchedCCC = watchedValues?.[1];
    const watchedNAF = watchedValues?.[2];
    const watchedCIF = watchedValues?.[3];
    const watchedEsAutonomo = watchedValues?.[4];
    const watchedProvinciaCodigo = watchedValues?.[5];
    const watchedProvinciaDescripcion = watchedValues?.[6];
    const esAutonomo = normalizeRelacionLaboralBoolean(watchedEsAutonomo);

    const fieldNameByKey = useMemo<Record<FieldKey, string>>(() => ({
        nombreEmpresa: names.nombreEmpresa,
        ccc: names.ccc,
        naf: names.naf,
        cif: names.cif,
    }), [names]);

    const watchedValuesByKey: Record<FieldKey, any> = {
        nombreEmpresa: watchedNombreEmpresa,
        ccc: watchedCCC,
        naf: watchedNAF,
        cif: watchedCIF,
    };

    useEffect(() => {
        if (!shouldUseRelacionesLaborales) {
            if (selectedEntornoId !== null) {
                setSelectedEntornoId(null);
            }
            return;
        }

        if (entornos.length === 0) {
            if (selectedEntornoId !== null) {
                setSelectedEntornoId(null);
            }

            if (selectorName) {
                setValue(selectorName, null, {
                    shouldDirty: false,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }

            return;
        }

        const selectedIdExists = selectedEntornoId !== null && entornos.some((entorno, index) => {
            return getEntornoOptionValue(entorno, index) === selectedEntornoId;
        });

        const nextSelectedId = selectedIdExists
            ? selectedEntornoId
            : getEntornoOptionValue(entornos[0], 0);

        if (nextSelectedId !== selectedEntornoId) {
            setSelectedEntornoId(nextSelectedId);
        }

        if (selectorName) {
            setValue(selectorName, nextSelectedId, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
            });
        }
    }, [entornos, selectedEntornoId, selectorName, getEntornoOptionValue, setValue, shouldUseRelacionesLaborales]);

    useEffect(() => {
        if (!shouldUseRelacionesLaborales) {
            return;
        }

        const keys = Object.keys(fieldNameByKey) as FieldKey[];

        keys.forEach((fieldKey) => {
            const fieldName = fieldNameByKey[fieldKey];
            const currentFormValue = watchedValuesByKey[fieldKey];
            const entornoValue = getEntornoFieldValue(selectedEntorno, fieldKey);
            const nextValue = normalizeEmptyValue(entornoValue);

            if (!areSameValues(currentFormValue, nextValue)) {
                setValue(fieldName, nextValue, {
                    shouldDirty: false,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }
        });

        if (names.esAutonomo) {
            const entornoEsAutonomo = normalizeRelacionLaboralBoolean(
                getFirstValueByKeys(selectedEntorno, mergedDataKeys.esAutonomo)
            );

            if (normalizeRelacionLaboralBoolean(watchedEsAutonomo) !== entornoEsAutonomo) {
                setValue(names.esAutonomo, entornoEsAutonomo, {
                    shouldDirty: false,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }
        }
    }, [
        shouldUseRelacionesLaborales,
        selectedEntorno,
        fieldNameByKey,
        getEntornoFieldValue,
        mergedDataKeys.esAutonomo,
        names.esAutonomo,
        watchedNombreEmpresa,
        watchedCCC,
        watchedNAF,
        watchedCIF,
        watchedEsAutonomo,
        setValue,
    ]);

    useEffect(() => {
        if (!shouldUseRelacionesLaborales || !shouldShowProvince || !names.provinciaCodigo || !names.provinciaDescripcion) {
            return;
        }

        const entornoProvinciaCodigo = normalizeEmptyValue(
            getFirstValueByKeys(selectedEntorno, mergedDataKeys.provinciaCodigo)
        );
        const entornoProvinciaDescripcion = normalizeEmptyValue(
            getFirstValueByKeys(selectedEntorno, mergedDataKeys.provinciaDescripcion)
        );
        const nextProvinciaCodigo = hasValue(entornoProvinciaCodigo) ? String(entornoProvinciaCodigo) : null;
        const shouldUpdateProvinciaCodigo = !areSameValues(watchedProvinciaCodigo, nextProvinciaCodigo);

        if (shouldUpdateProvinciaCodigo) {
            setValue(names.provinciaCodigo, nextProvinciaCodigo, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
            });

            if (!hasValue(entornoProvinciaDescripcion) && hasValue(watchedProvinciaDescripcion)) {
                setValue(names.provinciaDescripcion, null, {
                    shouldDirty: false,
                    shouldTouch: false,
                    shouldValidate: false,
                });
            }
        }

        if ((hasValue(entornoProvinciaDescripcion) || !hasValue(nextProvinciaCodigo))
            && !areSameValues(watchedProvinciaDescripcion, entornoProvinciaDescripcion)) {
            setValue(names.provinciaDescripcion, entornoProvinciaDescripcion, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
            });
        }
    }, [
        shouldUseRelacionesLaborales,
        shouldShowProvince,
        selectedEntorno,
        mergedDataKeys.provinciaCodigo,
        mergedDataKeys.provinciaDescripcion,
        names.provinciaCodigo,
        names.provinciaDescripcion,
        watchedProvinciaCodigo,
        watchedProvinciaDescripcion,
        setValue,
    ]);

    const handleEntornoChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextSelectedId = event.target.value || null;
        setSelectedEntornoId(nextSelectedId);

        if (selectorName) {
            setValue(selectorName, nextSelectedId, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
            });
        }
    };

    return (
        <>
            {selectorName && (
                <input type="hidden" {...register(selectorName)} />
            )}

            {names.esAutonomo && (
                <input
                    type="hidden"
                    {...register(names.esAutonomo, {
                        setValueAs: normalizeRelacionLaboralBoolean,
                    })}
                />
            )}

            {shouldUseRelacionesLaborales && entornos.length > 1 && (
                <div className={`col-xl-${xl} col-md-${md} col-12`}>
                    <div className="input-block">
                        <label htmlFor="RelacionesLaboralesSelector" className="input-block__label">
                            {mergedLabels.selector}
                        </label>
                        <select
                            id="RelacionesLaboralesSelector"
                            className="form-select"
                            value={selectedEntornoId || ''}
                            onChange={handleEntornoChange}
                        >
                            <option value="">{selectorPlaceholder}</option>
                            {entornos.map((entorno, index) => (
                                <option key={getEntornoOptionValue(entorno, index)} value={getEntornoOptionValue(entorno, index)}>
                                    {getEntornoOptionLabel(entorno, index)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            <FormInputText
                name={names.nombreEmpresa}
                id={names.nombreEmpresa}
                label={mergedLabels.nombreEmpresa}
                md={md}
                xl={xl}
                disabled={true}
                invisible={!hasValue(watchedNombreEmpresa)}
            />

            <FormInputText
                name={names.ccc}
                id={names.ccc}
                label={mergedLabels.ccc}
                md={md}
                xl={xl}
                disabled={true}
                invisible={esAutonomo === null ? !hasValue(watchedCCC) : esAutonomo}
            />

            <FormInputText
                name={names.naf}
                id={names.naf}
                label={mergedLabels.naf}
                md={md}
                xl={xl}
                disabled={true}
                invisible={esAutonomo === null ? !hasValue(watchedNAF) : !esAutonomo}
            />

            <FormInputText
                name={names.cif}
                id={names.cif}
                label={mergedLabels.cif}
                md={md}
                xl={xl}
                disabled={true}
                invisible={esAutonomo === null ? !hasValue(watchedCIF) : esAutonomo}
            />

            {shouldShowProvince && names.provinciaCodigo && names.provinciaDescripcion && (
                <div className="col-xl-12 col-md-12 col-12">
                    <Province
                        errors={errors}
                        register={register}
                        idCode={names.provinciaCodigo}
                        watchProvinceDescription={watchedProvinciaDescripcion}
                        idDescription={names.provinciaDescripcion}
                        watchProvinceCode={watchedProvinciaCodigo}
                        setValue={setValue}
                        disabled={true}
                    />
                </div>
            )}
        </>
    );
};

export default FormRelacionesLaborales;
